import createError from 'create-error';
import config from '../config/config';

const NetworkError = createError('NetworkError');
const ApiError = createError('ApiError');
const TimeoutError = createError('TimeoutError');

const defaultOptions = {
    parseJson: true,
    stringifyBody: true,
    contentType: 'application/json',
};

async function sendApiRequest(path, method = 'GET', body = undefined, options = {}) {
    options = {...defaultOptions, ...options};

    try {
        const response = await fetch(`${config.apiUrl}/${path}`, {
            method: method.toUpperCase(),
            // mode: 'cors',
            headers: options.contentType ? {'Content-Type': options.contentType} : undefined,
            body: body ? (options.stringifyBody ? JSON.stringify(body) : body) : undefined,
        });
        if (!response.ok) {
            throw new ApiError('request failed: ' + response.statusText + ' - ' + (await response.text()));
        }

        if (options.parseJson) {
            return response.json();
        }
        else {
            return response;
        }
    }
    catch (err) {
        throw new NetworkError(err.message ?? String(err));
    }
}

async function getUser() {
    return sendApiRequest(`user`);
}

async function updateUser(props) {
    return sendApiRequest(`user/update`, 'POST', props);
}

async function clearFaceData(props = {}) {
    return sendApiRequest(`user/clear-face-data`, 'POST', props);
}

async function requestUpload(props) {
    return sendApiRequest(`upload`, 'POST', props);
}

async function requestMultipartUploadPart(props) {
    return sendApiRequest(`upload/multipart/part`, 'POST', props);
}

async function completeMultipartUpload(props) {
    return sendApiRequest(`upload/multipart/complete`, 'POST', props);
}

async function abortMultipartUpload(props) {
    return sendApiRequest(`upload/multipart/abort`, 'POST', props);
}

async function submitJob(props) {
    return sendApiRequest(`jobs/submit`, 'POST', props);
}

async function getJobs(props = {}) {
    const {offset, limit, project_id, folder_id, sort_by, filter_by, person_ids} = props;

    const qs = new URLSearchParams({
        ...(offset !== undefined && { offset }),
        ...(limit !== undefined && { limit }),
        ...(project_id && { project_id }),
        ...(folder_id && { folder_id }),
        ...(sort_by && { sort_by }),
        ...(filter_by && { filter_by }),
        ...(person_ids?.length && { person_ids: person_ids.join(',') }),
    }).toString();
    return sendApiRequest(`jobs${qs ? '?' + qs : ''}`, 'GET');
}

async function getProcessingJobs() {
    return sendApiRequest('jobs/processing', 'GET');
}

async function getJob(jobId, { includeViewerContext = false, includePersons = false } = {}) {
    const includes = [];
    if (includeViewerContext) { includes.push('viewer'); }
    if (includePersons) { includes.push('persons'); }

    const qs = new URLSearchParams({
        ...(includes.length > 0 && { include: includes.join(',') }),
    }).toString();

    return sendApiRequest(`jobs/${jobId}${qs ? `?${qs}` : ''}`);
}

async function updateJob(jobId, props) {
    return sendApiRequest(`jobs/${jobId}/update`, 'POST', props);
}

async function createJobVariant(jobId, props) {
    return sendApiRequest(`jobs/${jobId}/variants/create`, 'POST', props);
}

async function createVersionBatch(props) {
    return sendApiRequest('jobs/versions/create-batch', 'POST', props);
}

async function getJobVideoUrls(jobId) {
    return sendApiRequest(`jobs/${jobId}/videos`);
}

async function deleteJobs(job_ids) {
    return sendApiRequest(`jobs/delete`, 'POST', {job_ids});
}

async function getPersonsForJobs({ job_ids, scope, match = 'intersection' } = {}) {
    return sendApiRequest('jobs/get-persons', 'POST', { job_ids, scope, match });
}

async function moveJobs(job_ids, project_id, from_project_id) {
    return sendApiRequest(`jobs/move`, 'POST', {job_ids, project_id, from_project_id});
}

async function zipJobs({job_ids, folder_ids, project_id}) {
    return sendApiRequest(`jobs/zip`, 'POST', {job_ids, folder_ids, project_id});
}

async function zipSharedLink(token, password = null) {
    return sendApiRequest(`share/zip`, 'POST', {token, password});
}

async function getProjects() {
    return sendApiRequest(`projects`);
}

async function getProject(project_id) {
    return sendApiRequest(`projects/${project_id}`);
}

async function getProjectFaces(project_id) {
    return sendApiRequest(`projects/${project_id}/faces`);
}

async function updateProject(project_id, props) {
    return sendApiRequest(`projects/${project_id}/update`, 'POST', props);
}

async function saveProjectFaces(project_id, props) {
    return sendApiRequest(`projects/${project_id}/faces/save`, 'POST', props);
}

async function deleteProject(project_id) {
    return sendApiRequest(`projects/${project_id}/delete`, 'POST');
}

async function createProject(props) {
    return sendApiRequest(`projects/create`, 'POST', props);
}

async function createFolder(project_id, props) {
    return sendApiRequest(`projects/${project_id}/folders/create`, 'POST', props);
}

async function updateFolder(project_id, folder_id, props) {
    return sendApiRequest(`projects/${project_id}/folders/${folder_id}/update`, 'POST', props);
}

async function deleteFolders(project_id, folder_ids) {
    return sendApiRequest(`projects/${project_id}/folders/delete`, 'POST', {folder_ids});
}

async function getStats() {
    return sendApiRequest(`stats`);
}

async function createSharedLink(selection) {
    const body = typeof selection === 'string' ? {job_id: selection} : selection;
    return sendApiRequest(`share`, 'POST', body);
}

async function updateSharedLink(props) {
    return sendApiRequest(`share/update`, 'POST', props);
}

async function getSharedLinks() {
    return sendApiRequest(`share`, 'GET');
}

async function deleteSharedLink(token) {
    return sendApiRequest(`share/delete`, 'POST', {token});
}

async function accessSharedLink(token, password) {
    return sendApiRequest(`share/access`, 'POST', {token, password});
}

async function sendShareEmail(props) {
    return sendApiRequest(`share/email`, 'POST', props);
}

async function deleteAccount() {
    return sendApiRequest(`user/delete`, 'POST');
}

async function updateUserPhoto(file) {
    const formData = new FormData();
    formData.append('file', file, file.name || 'logo');

    return sendApiRequest(`user/update-photo`, 'POST', formData, {
        stringifyBody: false,
        contentType: undefined,
    });
}

async function subscribeToPayments(purchase) {
    return sendApiRequest(`payment/subscribe`, 'POST', {purchase});
}

async function changePlan(planId) {
    return sendApiRequest(`payment/change-plan`, 'POST', {planId});
}

async function getBillingSummary() {
    return sendApiRequest(`payment/billing-summary`, 'GET');
}

async function cancelSubscription() {
    return sendApiRequest(`payment/cancel-subscription`, 'POST');
}

async function getJobDetections(job_id) {
    return sendApiRequest(`jobs/${job_id}/detections`, 'GET');
}

function adminQs(props = {}) {
    return new URLSearchParams(
        Object.entries(props).filter(([, value]) => value !== undefined && value !== null && value !== '')
    ).toString();
}

async function getAdminUsers(props = {}) {
    const qs = adminQs(props);
    return sendApiRequest(`admin/users${qs ? '?' + qs : ''}`, 'GET');
}

async function updateAdminUser(userId, props) {
    return sendApiRequest(`admin/users/${encodeURIComponent(userId)}/update`, 'POST', props);
}

async function deleteAdminUser(userId) {
    return sendApiRequest(`admin/users/${encodeURIComponent(userId)}/delete`, 'POST');
}

async function getAdminJobs(props = {}) {
    const qs = adminQs(props);
    return sendApiRequest(`admin/jobs${qs ? '?' + qs : ''}`, 'GET');
}

async function getAdminSharedLinks(props = {}) {
    const qs = adminQs(props);
    return sendApiRequest(`admin/shared-links${qs ? '?' + qs : ''}`, 'GET');
}

export {
    getUser, updateUser, clearFaceData,
    updateUserPhoto,
    deleteAccount,
    requestUpload,
    requestMultipartUploadPart, completeMultipartUpload, abortMultipartUpload,
    submitJob,
    getJobs, getProcessingJobs, getJob, updateJob, createJobVariant, createVersionBatch, deleteJobs, moveJobs, getPersonsForJobs,
    getJobVideoUrls,
    getJobDetections,
    zipJobs, zipSharedLink,
    createProject, getProjects, getProject, getProjectFaces, updateProject, deleteProject,
    saveProjectFaces,
    createFolder, updateFolder, deleteFolders,
    getStats,
    createSharedLink, updateSharedLink, getSharedLinks, deleteSharedLink, sendShareEmail, accessSharedLink,
    subscribeToPayments, changePlan, getBillingSummary, cancelSubscription,
    getAdminUsers, updateAdminUser, deleteAdminUser, getAdminJobs, getAdminSharedLinks,
    NetworkError, ApiError, TimeoutError,
};
